(() => {
  const WEEKDAYS = [1, 2, 3, 4, 5];

  function londonNow() {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      }).formatToParts(new Date()).map((p) => [p.type, p.value])
    );

    return new Date(Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    ));
  }

  function formatDateKey(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function parseHolidays(value) {
    if (!value) return new Set();

    return new Set(
      value
        .split(/[\n,]+/)
        .map((date) => date.trim())
        .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    );
  }

  function createBusinessDayChecker(holidays) {
    return function isBusinessDay(date) {
      return WEEKDAYS.includes(date.getUTCDay()) && !holidays.has(formatDateKey(date));
    };
  }

  function isBeforeCutoff(now, cutoffHour, cutoffMinute, isBusinessDay) {
    return isBusinessDay(now) &&
      (
        now.getUTCHours() < cutoffHour ||
        (now.getUTCHours() === cutoffHour && now.getUTCMinutes() < cutoffMinute)
      );
  }

  function nextBusinessDay(date, isBusinessDay) {
    const d = new Date(date);
    do {
      d.setUTCDate(d.getUTCDate() + 1);
    } while (!isBusinessDay(d));
    return d;
  }

  function getDispatchDate(now, cutoffHour, cutoffMinute, isBusinessDay) {
    const afterCutoff =
      now.getUTCHours() > cutoffHour ||
      (now.getUTCHours() === cutoffHour && now.getUTCMinutes() >= cutoffMinute);

    if (isBusinessDay(now) && !afterCutoff) return new Date(now);
    return nextBusinessDay(now, isBusinessDay);
  }

  function getDeliveryDate(dispatchDate, isBusinessDay) {
    return nextBusinessDay(dispatchDate, isBusinessDay);
  }

  function getCountdownTarget(now, cutoffHour, cutoffMinute, isBusinessDay) {
    if (isBeforeCutoff(now, cutoffHour, cutoffMinute, isBusinessDay)) {
      const target = new Date(now);
      target.setUTCHours(cutoffHour, cutoffMinute, 0, 0);
      return target;
    }

    const target = nextBusinessDay(now, isBusinessDay);
    target.setUTCHours(cutoffHour, cutoffMinute, 0, 0);
    return target;
  }

  function ordinal(n) {
    const suffixes = ["th", "st", "nd", "rd"];
    const value = n % 100;
    return `${n}${suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]}`;
  }

  function formatDeliveryDate(date) {
    const weekday = new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" }).format(date);
    const month = new Intl.DateTimeFormat("en-GB", { month: "long", timeZone: "UTC" }).format(date);
    return `${weekday}, ${ordinal(date.getUTCDate())} ${month}`;
  }

  function formatCountdown(ms) {
    const totalMinutes = Math.max(0, Math.floor(ms / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}hr ${minutes}mins`;
  }

  function isVariantInStock(available, count, inventoryPolicy) {
    if (!inventoryPolicy && !available && count <= 0) {
      return false;
    }

    return available && count > 0;
  }

  if (!customElements.get("shipping-urgency")) {
    class ShippingUrgency extends HTMLElement {
      constructor() {
        super();
        this.timer = null;
      }

      connectedCallback() {
        this.dateEl = this.querySelector("[data-edd-date]");
        this.countdownEl = this.querySelector("[data-edd-countdown]");
        this.dispatchDateEl = this.querySelector("[data-edd-dispatch-date]");
        this.dispatchToday = this.querySelector("[data-dispatch-today]");
        this.dispatchNext = this.querySelector("[data-dispatch-next]");
        this.shippingSuffix = this.querySelector("[data-shipping-suffix]");
        this.promoUpsell = this.querySelector("[data-promo-upsell]");
        this.promoQualified = this.querySelector("[data-promo-qualified]");
        this.variantInventory = this.getVariantInventory();
        this.holidays = parseHolidays(this.dataset.holidays);
        this.isBusinessDay = createBusinessDayChecker(this.holidays);

        const productInfo = this.closest(".product-info");
        if (productInfo) {
          productInfo.addEventListener("on:variant:change", this.handleVariantChange.bind(this));
        }

        this.updateVariantState(
          Number(this.dataset.variantPrice),
          this.dataset.variantAvailable === "true",
          Number(this.dataset.inventoryQuantity),
          this.dataset.inventoryPolicy
        );

        this.renderDates();
        this.timer = setInterval(() => {
          if (!this.isConnected) {
            clearInterval(this.timer);
            return;
          }

          if (!this.hidden) {
            this.renderDates();
          }
        }, 30000);
      }

      disconnectedCallback() {
        if (this.timer) {
          clearInterval(this.timer);
        }
      }

      getVariantInventory() {
        const dataEl = this.querySelector('[type="application/json"]');
        return dataEl ? JSON.parse(dataEl.textContent) : [];
      }

      handleVariantChange(evt) {
        if (evt.detail.variant) {
          const inventory = this.variantInventory.find(
            (variant) => variant.id === evt.detail.variant.id
          );

          if (inventory) {
            this.updateVariantState(
              inventory.price,
              inventory.available,
              inventory.inventory_quantity,
              inventory.inventory_policy
            );
            return;
          }
        }

        this.updateVariantState(0, false, 0, "");
      }

      updateVariantState(price, available, count, inventoryPolicy) {
        const inStock = isVariantInStock(available, count, inventoryPolicy);
        this.hidden = !inStock;

        if (!inStock) {
          return;
        }

        const threshold = Number(this.dataset.threshold || 3000);
        const belowThreshold = price < threshold;

        if (this.shippingSuffix) {
          this.shippingSuffix.hidden = !belowThreshold;
        }

        if (this.promoUpsell) {
          this.promoUpsell.hidden = !belowThreshold;
        }

        if (this.promoQualified) {
          this.promoQualified.hidden = belowThreshold;
        }

        this.renderDates();
      }

      renderDates() {
        if (!this.dateEl) return;

        const cutoffHour = Number(this.dataset.cutoffHour || 15);
        const cutoffMinute = Number(this.dataset.cutoffMinute || 0);
        const now = londonNow();
        const dispatchDate = getDispatchDate(now, cutoffHour, cutoffMinute, this.isBusinessDay);
        const deliveryDate = getDeliveryDate(dispatchDate, this.isBusinessDay);
        const beforeCutoff = isBeforeCutoff(now, cutoffHour, cutoffMinute, this.isBusinessDay);

        this.dateEl.textContent = formatDeliveryDate(deliveryDate);

        if (this.dispatchToday) {
          this.dispatchToday.hidden = !beforeCutoff;
        }

        if (this.dispatchNext) {
          this.dispatchNext.hidden = beforeCutoff;
        }

        if (this.dispatchDateEl) {
          this.dispatchDateEl.textContent = formatDeliveryDate(dispatchDate);
        }

        if (beforeCutoff && this.countdownEl) {
          const countdownTarget = getCountdownTarget(now, cutoffHour, cutoffMinute, this.isBusinessDay);
          this.countdownEl.textContent = formatCountdown(countdownTarget - now);
        }
      }
    }

    customElements.define("shipping-urgency", ShippingUrgency);
  }

  function initLegacyWidgets(root = document) {
    root.querySelectorAll("[data-edd]:not([data-edd-initialized])").forEach((el) => {
      el.dataset.eddInitialized = "true";

      const dateEl = el.querySelector("[data-edd-date]");
      const countdownEl = el.querySelector("[data-edd-countdown]");
      if (!dateEl || !countdownEl) return;

      const holidays = parseHolidays(el.dataset.holidays);
      const isBusinessDay = createBusinessDayChecker(holidays);

      const render = () => {
        const cutoffHour = Number(el.dataset.cutoffHour || 15);
        const cutoffMinute = Number(el.dataset.cutoffMinute || 0);
        const now = londonNow();
        const dispatchDate = getDispatchDate(now, cutoffHour, cutoffMinute, isBusinessDay);
        const deliveryDate = getDeliveryDate(dispatchDate, isBusinessDay);
        const countdownTarget = getCountdownTarget(now, cutoffHour, cutoffMinute, isBusinessDay);

        dateEl.textContent = formatDeliveryDate(deliveryDate);
        countdownEl.textContent = formatCountdown(countdownTarget - now);
      };

      render();

      const timer = setInterval(() => {
        if (!el.isConnected) {
          clearInterval(timer);
          return;
        }

        render();
      }, 30000);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initLegacyWidgets());
  } else {
    initLegacyWidgets();
  }

  document.addEventListener("shopify:section:load", (event) => initLegacyWidgets(event.target));
})();
